import 'reflect-metadata';
import {
  PathParamMetadataKey,
  BodyMetadataKey,
  QueryParamMetadataKey,
  UploadProgressMetadataKey,
  BaseUrlParamMetadataKey,
  HeaderParamMetadataKey
} from './constants';
import { resolveRouteUrl } from './resolver';
import execute from './executor';
import Parameter from '$shared/models/api/Parameter';
import ApiError from '$shared/models/api/ApiError';

interface DecoratorExecutor {
  (target: unknown, propertyKey: string, descriptor: PropertyDescriptor): PropertyDescriptor;
}

function prepareAndExecute(method: string, route: string) {
  return function (target: any, propertyName: string, descriptor: PropertyDescriptor) {
    descriptor.value = function (...args: any) {
      const baseUrlParamIndex = Reflect.getOwnMetadata(
        BaseUrlParamMetadataKey,
        target,
        propertyName
      );
      const bodyParamIndex = Reflect.getOwnMetadata(BodyMetadataKey, target, propertyName);
      const uploadCallbackParamIndex = Reflect.getOwnMetadata(
        UploadProgressMetadataKey,
        target,
        propertyName
      );
      const overriddenBaseURL =
        baseUrlParamIndex != undefined && baseUrlParamIndex > -1
          ? args[baseUrlParamIndex]
          : undefined;
      const endPoint = resolveRouteUrl(target, propertyName, route, args, this, overriddenBaseURL);

      const headers: any = {};
      const headerParams: Array<Parameter> = Reflect.getOwnMetadata(
        HeaderParamMetadataKey,
        target,
        propertyName
      );
      if (headerParams) {
        headerParams.forEach((param) => {
          headers[param.key] = args[param.paramIndex];
        });
      }
      if (this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
      }
      if (this.xImpersonateOrg) {
        headers['X-ImpersonateOrg'] = this.xImpersonateOrg;
      }
      return execute(
        endPoint,
        method,
        headers,
        bodyParamIndex !== undefined && bodyParamIndex > -1 ? args[bodyParamIndex] : undefined,
        args[uploadCallbackParamIndex]
      );
    };
    return descriptor;
  };
}

function Authenticated(): DecoratorExecutor {
  return function (target: unknown, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    descriptor.value = function (...args) {
      if (this.accessToken) {
        if (args instanceof Array) {
          return originalMethod.apply(this, args);
        }
        return originalMethod.apply(this, ...args);
      }
      throw new ApiError(401, 'Unauthorized');
    };
    return descriptor;
  };
}

function Post(route: string): DecoratorExecutor {
  return prepareAndExecute('POST', route);
}

function Head(route: string): DecoratorExecutor {
  return prepareAndExecute('HEAD', route);
}

function Delete(route: string): DecoratorExecutor {
  return prepareAndExecute('DELETE', route);
}

function Get(route: string): DecoratorExecutor {
  return prepareAndExecute('GET', route);
}

function Put(route: string): DecoratorExecutor {
  return prepareAndExecute('PUT', route);
}

function Patch(route: string): DecoratorExecutor {
  return prepareAndExecute('PATCH', route);
}

function PathParam(key: string) {
  return function (target: unknown, propertyKey: string | symbol, paramIndex: number): void {
    const pathParams: Parameter[] =
      Reflect.getOwnMetadata(PathParamMetadataKey, target, propertyKey) || [];
    pathParams.push(new Parameter(key, paramIndex));
    Reflect.defineMetadata(PathParamMetadataKey, pathParams, target, propertyKey);
  };
}

function Header(key: string) {
  return function (target: unknown, propertyKey: string | symbol, paramIndex: number): void {
    const headerParams: Parameter[] =
      Reflect.getOwnMetadata(HeaderParamMetadataKey, target, propertyKey) || [];
    headerParams.push(new Parameter(key, paramIndex));
    Reflect.defineMetadata(HeaderParamMetadataKey, headerParams, target, propertyKey);
  };
}

function QueryParam(key: string) {
  return function (target: unknown, propertyKey: string | symbol, paramIndex: number): void {
    const queryParams: Parameter[] =
      Reflect.getOwnMetadata(QueryParamMetadataKey, target, propertyKey) || [];
    queryParams.push(new Parameter(key, paramIndex));
    Reflect.defineMetadata(QueryParamMetadataKey, queryParams, target, propertyKey);
  };
}

function Body(target: unknown, propertyKey: string | symbol, paramIndex: number): void {
  let bodyParamIndex = Reflect.getOwnMetadata(BodyMetadataKey, target, propertyKey) || -1;
  if (bodyParamIndex !== -1) {
    throw new Error('Only one body parameter is allowed - ' + propertyKey.toString());
  }
  bodyParamIndex = paramIndex;
  Reflect.defineMetadata(BodyMetadataKey, bodyParamIndex, target, propertyKey);
}

function BaseUrl(target: unknown, propertyKey: string | symbol, paramIndex: number): void {
  let baseUrlParamIndex =
    Reflect.getOwnMetadata(BaseUrlParamMetadataKey, target, propertyKey) || -1;
  if (baseUrlParamIndex !== -1) {
    throw new Error('Only one body parameter is allowed - ' + propertyKey.toString());
  }
  baseUrlParamIndex = paramIndex;
  Reflect.defineMetadata(BaseUrlParamMetadataKey, baseUrlParamIndex, target, propertyKey);
}

function UploadCallback(target: unknown, propertyKey: string | symbol, paramIndex: number): void {
  let callbackParamIndex =
    Reflect.getOwnMetadata(UploadProgressMetadataKey, target, propertyKey) || -1;
  if (callbackParamIndex !== -1) {
    throw new Error('Only one callback parameter is allowed - ' + propertyKey.toString());
  }
  callbackParamIndex = paramIndex;
  Reflect.defineMetadata(UploadProgressMetadataKey, callbackParamIndex, target, propertyKey);
}

export {
  Authenticated,
  Post,
  Put,
  Head,
  Get,
  Patch,
  Delete,
  Body,
  BaseUrl,
  PathParam,
  QueryParam,
  Header,
  UploadCallback
};
